import { paths } from "../routes/paths";

/**
 * WEB twin of the mobile entry-popup screen map
 * (Admin-App/src/whatsNew/entryPopupScreens.js).
 *
 * The backend owns the whitelist of `screenKey`s the super-admin can pick from
 * (Backend/src/config/entryPopupScreens.js — key + Hebrew label); THIS file owns
 * where each key actually lands in the dashboard. Keeping the two apart is the
 * whole safety property: a key this build does not recognise (an older app, a
 * renamed route, a typo, a mobile-only screen) simply does nothing instead of
 * dumping the user on the wrong page.
 *
 * Backend whitelist (all 8 mapped):
 *   invoicing        "קופה"          → /documents        (POS tab "קופה")
 *   storeAndPayments "חנות ותשלומים"  → NO WEB TWIN — deliberately unmapped
 *   gatewaySettings  "הגדרות סליקה"   → /settings/payment
 *   products         "מוצרים"         → /products
 *   subscription     "שדרוג חבילה"    → /subscription
 *   billing          "חיוב ושימוש"    → /invoices         ("המנוי שלי וחשבוניות")
 *   settings         "הגדרות"         → /settings
 *   calendar         "יומן"           → /appointments     ("יומן תורים")
 *
 * The remaining keys below are the extra ones the mobile map already carries.
 * They are not in the backend whitelist today, so a slide cannot be saved with
 * them — they are here so that widening the whitelist later Just Works on web.
 */
export const ENTRY_POPUP_SCREEN_PATHS = {
  // ── backend whitelist ────────────────────────────────────────────────────
  calendar: paths.appointments,
  invoicing: paths.documents,
  gatewaySettings: paths.paymentSettings,
  products: paths.products,
  subscription: paths.subscription,
  billing: paths.invoices,
  settings: paths.settings,
  // storeAndPayments — the mobile "חנות ותשלומים" hub (in-app store + plan
  // cards) has no dashboard equivalent. Left OUT on purpose: a no-op is the
  // required degradation, and every candidate here (products / purchases /
  // subscription) would be a different screen than the slide promised.

  // ── mobile-map extras, mapped where the dashboard has a real twin ────────
  whatsappBot: paths.whatsappBot,
  cashRegister: paths.cashRegister,
  paymentRequests: paths.paymentRequests,
  coupons: paths.coupons,
  giftCards: paths.giftCards,
  automations: paths.automations,
  messages: paths.createNotifications, // mobile "Messages" = send-an-update
  reminders: paths.reminders,
  reviews: paths.reviews,
  employees: paths.employees,
  statistics: paths.stats,
  profile: paths.profile,
  // transactions — mobile TransactionsHistory (gateway transaction log) has no
  // 1:1 dashboard screen; /purchases is client purchases, not the same thing.
  // Unmapped on purpose.
};

/**
 * Resolve a slide's `screenKey` to a dashboard route path.
 * Never throws.
 *
 * @param {string} screenKey
 * @returns {string|null} the route path, or null when there is no web twin
 */
export function resolveScreenKeyPath(screenKey) {
  try {
    if (!screenKey || typeof screenKey !== "string") return null;
    const path = ENTRY_POPUP_SCREEN_PATHS[screenKey];
    return typeof path === "string" && path ? path : null;
  } catch (_e) {
    return null;
  }
}

/**
 * Navigate to the screen mapped from `screenKey`, if any. Never throws, and
 * never navigates on an unknown key.
 *
 * @param {Function} navigate   react-router's useNavigate() result
 * @param {string}   screenKey  a key from the backend whitelist
 * @returns {boolean}           true only if a navigation was actually dispatched
 */
export function navigateToScreenKey(navigate, screenKey) {
  try {
    if (typeof navigate !== "function") return false;
    const path = resolveScreenKeyPath(screenKey);
    if (!path) return false; // unknown key → do nothing
    navigate(path);
    return true;
  } catch (_e) {
    // Never let a navigation error bubble out of the popup.
    return false;
  }
}
